/**
 * Round 95q (Jun 2026) — Country flag emoji helper.
 *
 * Used in the Stock Modal header, the stock table row, and the
 * portfolio row's ADR badge so users immediately see which
 * country's tax-treaty rules apply to an ADR. Withholding rates
 * vary 0%–30% by country, so the flag is genuinely useful info
 * (UK 0% on most ADRs, France 30%, Netherlands 15%, etc.).
 *
 * Input: ISO 3166-1 alpha-2 country code (e.g. "GB", "JP", "KZ").
 * Output: regional-indicator flag emoji string or empty string
 * for invalid / missing input.
 */
export function ccToFlag(cc) {
  if (!cc || typeof cc !== 'string' || cc.length !== 2) return '';
  const upper = cc.toUpperCase();
  // Each ASCII letter A-Z (65-90) maps to a regional indicator
  // symbol at 0x1F1E6 + (letter - 'A'). Adjacent pairs render as
  // the country flag on every modern browser/OS that supports
  // emoji.
  const base = 0x1F1E6;
  const a = upper.charCodeAt(0) - 65;
  const b = upper.charCodeAt(1) - 65;
  if (a < 0 || a > 25 || b < 0 || b > 25) return '';
  return String.fromCodePoint(base + a) + String.fromCodePoint(base + b);
}

// Pretty country name for the tooltip — kept small; just the
// codes the backend's static ADR_COUNTRY map can emit. Anything
// else falls back to the bare code.
const COUNTRY_NAMES = {
  GB: 'United Kingdom', JP: 'Japan', CN: 'China', TW: 'Taiwan',
  KR: 'South Korea', KZ: 'Kazakhstan', NL: 'Netherlands',
  CH: 'Switzerland', DE: 'Germany', FR: 'France', SE: 'Sweden',
  NO: 'Norway', DK: 'Denmark', ES: 'Spain', IT: 'Italy', IE: 'Ireland',
  AU: 'Australia', NZ: 'New Zealand', BR: 'Brazil', MX: 'Mexico',
  AR: 'Argentina', CL: 'Chile', CO: 'Colombia', PE: 'Peru',
  IN: 'India', IL: 'Israel', RU: 'Russia', ID: 'Indonesia',
  PH: 'Philippines', SG: 'Singapore', HK: 'Hong Kong',
  ZA: 'South Africa', LU: 'Luxembourg', BE: 'Belgium',
  FI: 'Finland', AT: 'Austria', US: 'United States',
};

export function ccName(cc) {
  if (!cc) return '';
  return COUNTRY_NAMES[cc.toUpperCase()] || cc.toUpperCase();
}
